import { SHA256 } from 'crypto-js';
import { fromJS } from 'immutable';
import * as KurentoUtils from 'kurento-utils';
import { FALLBACK_KMS_SERVER } from 'flow/utils/socket-utils';
import { rtcStateChangeEventHandler } from 'flow/utils/rtc-utils';
import {
    applyScreenShareEncoding,
    preferVp8InSdp,
} from 'flow/utils/webrtc-codec-utils';
import {
    getKmsServerFromConfig,
    MeetingLogLevel,
    addMeetingLog,
    parseGetParams,
    isVideoStatsDebugEnabled,
    isVideoStatsUploadEnabled,
    sliceUUIDFromUserName,
} from 'flow/utils/meeting-utils';
import {
    AdmissionDeniedCause,
    WaitingRoomStanding,
    PeerType,
} from 'flow/utils/meeting-constants';
import { CANVAS_SETTINGS } from 'flow/constants/video-background-constants';
import {
    selectActiveMeetingCode,
    selectActiveMeetingMembers,
    selectJoinExpectedToBeHeld,
    selectSenderName,
    selectActiveMeetingScreenShareMemberName,
    selectSenderAudioActive,
} from 'flow/selectors/meeting-selectors';
import {
    toggleTrackChange,
    fetchMeetingData,
    stopScreenShare,
    setActiveMeetingScreenShareMemberNameAction,
    forceMuteSenderVideo,
    muteAudio,
    endMeeting,
} from 'flow/actions/meeting-actions';
import { setSnackbar } from 'flow/actions/app-actions';
import { playParticipantJoinedAlert } from 'flow/utils/meeting-sounds';
import {
    memberIsReturningFromReconnect,
    rememberMemberLeft,
} from 'flow/utils/meeting-reconnect-window';
import { handleRoomNotJoined } from 'flow/actions/meeting-lobby-actions';
import {
    setVideoWebSocketAction,
    setVideoJoinRoomPromiseAction,
    setVideoNewMeetingVideoMemberAction,
    setVideoMemberPrimaryPeerAction,
    removeVideoMemberAction,
    setVideoActivityListAction,
    setSpeakingMembersAction,
    setVideoMemberPeerSetupAttemptsAction,
    setVideoSenderPrimaryPeerAction,
    setVideoSenderSecondaryPeerAction,
    setVideoConnectionStateAction,
    setVideoSenderReconnectionTokenAction,
    setVideoMemberSecondaryPeerAction,
    setWaitingParticipantsAction,
    setOwnWaitingRoomStandingAction,
} from 'flow/actions/video-actions';
import {
    selectVideoWebSocket,
    selectVideoJoinRoomPromise,
    selectVideoSenderVideoStream,
    selectVideoSenderPrimaryPeer,
    selectVideoMemberPrimaryPeer,
    selectVideoMembers,
    selectVideoSenderVideoActive,
    selectVideoSenderScreenShareActive,
    selectVideoMemberPeerSetupAttempts,
    selectVideoSecondarySenderVideoStream,
    selectVideoSecondarySenderPeer,
    selectVideoMemberSecondaryPeer,
    selectVideoMemberByName,
    selectOwnWaitingRoomStanding,
} from 'flow/selectors/video-selectors';
import {
    removePeerFromStats,
    addPeerForStats,
    startMetricsCollection,
    DEBUG_STATS_POLL_INTERVAL_MS,
    PRODUCTION_STATS_POLL_INTERVAL_MS,
} from 'flow/utils/video-log-utils';
import appConfig from 'app-config';

export const createVideoSignalingWebSocket =
    () => async (dispatch, getState) => {
        const webSocketUrl = await dispatch(getWebsocketUrl());
        return new Promise((resolve, reject) => {
            const webSocket = new WebSocket(webSocketUrl);
            webSocket.onclose = (closeEvent) => {
                const closeCode = closeEvent && closeEvent.code;
                const closeReason = (closeEvent && closeEvent.reason) || '';
                console.warn(
                    'Video signaling closed on setup:',
                    closeCode,
                    closeReason,
                );
                dispatch(
                    addMeetingLog(
                        `VideoSignalingClientSocket#setupOnclose: Video signaling closed: ${JSON.stringify(
                            closeEvent,
                        )}`,
                    ),
                    MeetingLogLevel.WARNING,
                );
                return reject({ closeCode, closeReason });
            };
            webSocket.onerror = (error) => {
                dispatch(
                    addMeetingLog(
                        `VideoSignalingClientSocket#setupOnerror: Socket error: ${JSON.stringify(
                            error,
                        )}`,
                    ),
                    MeetingLogLevel.ERROR,
                );
                console.error('Video signaling error on setup:', error);
                return reject({ error });
            };
            webSocket.onmessage = (message) =>
                dispatch(handleSignalingMessage(message));
            webSocket.onopen = () => {
                webSocket.onclose = (closeEvent) => {
                    const closeCode = closeEvent && closeEvent.code;
                    const closeReason = (closeEvent && closeEvent.reason) || '';
                    dispatch(
                        addMeetingLog(
                            `VideoSignalingClientSocket#onclose: Video signaling closed: ${JSON.stringify(
                                closeReason,
                            )}`,
                        ),
                        MeetingLogLevel.WARNING,
                    );
                    console.warn(
                        'Video signaling closed:',
                        closeCode,
                        closeReason,
                    );
                    // Only clear if this socket is still the active one — during a
                    // reconnect the old socket may close after a new one is in place.
                    if (selectVideoWebSocket(getState()) === webSocket) {
                        dispatch(setVideoWebSocketAction(null));
                    }
                };
                webSocket.onerror = (error) => {
                    dispatch(
                        addMeetingLog(
                            `VideoSignalingClientSocket#onerror: Socket error: ${JSON.stringify(
                                error,
                            )}`,
                        ),
                        MeetingLogLevel.ERROR,
                    );
                    console.error('Video signaling error:', error);
                };

                dispatch(setVideoWebSocketAction(webSocket));
                dispatch(
                    addMeetingLog(
                        'VideoSignalingClientSocket successfully set up socket',
                    ),
                );
                return resolve();
            };
        });
    };

const getWebsocketUrl = () => async (dispatch) => {
    if (appConfig.localVideoSignaling) {
        return 'ws://localhost:8443/groupcall';
    }
    const parsedParams = parseGetParams();
    const signalingServerFromURL =
        parsedParams && parsedParams.signallingServer;
    if (signalingServerFromURL) {
        if (parseInt(signalingServerFromURL, 10)) {
            return `wss://kms${signalingServerFromURL}.telavox.se:8443/groupcall`;
        }

        return `wss://${signalingServerFromURL}/groupcall`;
    }

    const signalingServerFromConfig = await dispatch(getKmsServerFromConfig());
    if (signalingServerFromConfig) {
        return `wss://${signalingServerFromConfig.get(
            'publicUrl',
        )}:${signalingServerFromConfig.get('websocketPort')}/groupcall`;
    }

    return `wss://${FALLBACK_KMS_SERVER.publicUrl}:${FALLBACK_KMS_SERVER.websocketPort}/groupcall`;
};

// Two separate budgets, because the two waits are nothing alike.
//
// Joining is work the client and server are actively doing — connecting,
// negotiating media — and keeps the deadline it has always had. Waiting at the
// door (VID-76) is work a *person* has to do: notice the request and decide.
// That deserves minutes, and exists only as a backstop for a server that never
// answers.
//
// Signaling expires the lobby itself after video_signaling.lobby_admission_timeout_ms
// (3 minutes by default) on a 30-second sweep, so it gives up at 3:30 at the
// latest. Staying well clear of that keeps the server's TIMED_OUT the thing
// people normally see, and stops us ending a wait the meeting is still holding
// open.
const JOIN_TIMEOUT_MS = 10000;
const ADMISSION_WAIT_TIMEOUT_MS = 300000;
const JOIN_TIMEOUT_TICK_MS = 500;

export const joinVideoRoom =
    (reconnectionToken) => async (dispatch, getState) => {
        const joinPromise = dispatch(joinVideoRoomPromise(reconnectionToken));
        // Suppress an unhandled-rejection warning if the timeout wins the race
        // and the stored joinRoomPromise is rejected after the race resolves.
        joinPromise.catch(() => {});
        let timeoutHandle;
        const timeoutPromise = new Promise((_resolve, rejectTimeout) => {
            // Tick rather than a flat setTimeout, so the two budgets can run
            // one at a time: whichever wait we are actually in.
            let joinElapsed = 0;
            let admissionWaitElapsed = 0;
            timeoutHandle = setInterval(() => {
                if (
                    selectOwnWaitingRoomStanding(getState()) ===
                    WaitingRoomStanding.WAITING
                ) {
                    admissionWaitElapsed += JOIN_TIMEOUT_TICK_MS;
                    if (admissionWaitElapsed >= ADMISSION_WAIT_TIMEOUT_MS) {
                        // Report it as the server would have, so the lobby says
                        // nobody answered rather than offering a reload.
                        dispatch(
                            setOwnWaitingRoomStandingAction(
                                WaitingRoomStanding.DENIED,
                                AdmissionDeniedCause.TIMED_OUT,
                            ),
                        );
                        rejectTimeout(new Error('admission wait timed out'));
                        return;
                    }
                    // The join has not started yet, so its budget stays unspent
                    // and runs in full once we are let in.
                    joinElapsed = 0;
                    return;
                }
                joinElapsed += JOIN_TIMEOUT_TICK_MS;
                if (joinElapsed >= JOIN_TIMEOUT_MS) {
                    rejectTimeout(new Error('joinVideoRoom timed out'));
                }
            }, JOIN_TIMEOUT_TICK_MS);
        });
        try {
            await Promise.race([joinPromise, timeoutPromise]);
        } catch (e) {
            const joinRoomPromise = selectVideoJoinRoomPromise(getState());
            if (joinRoomPromise) {
                const name = selectSenderName(getState());
                const meetingCode = selectActiveMeetingCode(getState());
                dispatch(
                    addMeetingLog(
                        `VideoSignalingClientSocket#joinVideoRoom, promise for ${name} joining room with meetingCode ${meetingCode} timed out and was rejected`,
                    ),
                );
                joinRoomPromise.reject();
                dispatch(setVideoJoinRoomPromiseAction(null));
            }
            throw e;
        } finally {
            clearInterval(timeoutHandle);
        }
        // Only once the join has actually resolved. Starting before it meant a
        // failed join (whose failure paths mostly never reach stopMeeting ->
        // stopMetricsCollection) left the poll ticking for the rest of the SPA
        // session, with uploadEnabled frozen from the failed attempt. Idempotent
        // across reconnects — the collector keeps its running interval.
        startMetricsCollection(selectSenderName(getState()), {
            uploadEnabled: isVideoStatsUploadEnabled(),
            // Only debug sessions register peers, so only they have anything
            // to gain from the 2s tick; everyone else keeps the 5s timer that
            // just drives the 10s disconnect-only send. The debug gate covers
            // what the tick does — this covers how often it runs.
            pollIntervalMs: isVideoStatsDebugEnabled()
                ? DEBUG_STATS_POLL_INTERVAL_MS
                : PRODUCTION_STATS_POLL_INTERVAL_MS,
        });
    };

const joinVideoRoomPromise = (reconnectionToken) => (dispatch, getState) => {
    /**
     * This promise is resolved in handleSignalingMessage#roomJoined
     * or rejected in handleSignalingMessage#roomNotJoined
     */
    return new Promise((resolve, reject) => {
        dispatch(setVideoJoinRoomPromiseAction({ resolve, reject }));
        const meetingCode = selectActiveMeetingCode(getState());
        const room = SHA256(meetingCode).toString();
        const name = selectSenderName(getState());
        // TODO: #video do we need to set roomName to store?
        dispatch(
            addMeetingLog(
                `VideoSignalingClientSocket#joinVideoRoomPromise, ${name} join room with meetingCode ${meetingCode}`,
            ),
        );

        dispatch(
            sendMessage({
                id: 'joinRoom',
                room,
                name,
                reconnectionToken,
                // Declares that being held in the lobby is survivable here — that
                // awaitingApproval, admissionDenied and awaitingParticipants are
                // understood (VID-76). Signaling holds nobody who omits it, so
                // clients that predate the lobby keep joining as they always did.
                admissionAware: true,
            }),
        );
    });
};

export const leaveVideoRoom = () => (dispatch, getState) => {
    const videoMembers = selectVideoMembers(getState());
    dispatch(
        addMeetingLog(
            'VideoSignalingClientSocket#leaveVideoRoom, leaving and disposing others peers',
        ),
    );
    // videoMembers is an Immutable Map keyed by member name; forEach yields
    // (value, key), so the name — which the peer selectors expect — is the
    // SECOND argument (the first is the member's data Map). Dispose BOTH receive
    // legs: the primary (camera, or screen while that member is sharing) and the
    // secondary (a sharer's camera), which was previously never torn down here.
    // Reset each slot to {} so no disposed peer lingers in the store — this path
    // also runs on reconnect, where a stale peer reference would otherwise stay.
    videoMembers.forEach((_memberData, name) => {
        const primaryPeer = selectVideoMemberPrimaryPeer(getState(), name);
        if (typeof primaryPeer?.dispose === 'function') {
            primaryPeer.dispose();
            dispatch(setVideoMemberPrimaryPeerAction(name, {}));
        }
        const secondaryPeer = selectVideoMemberSecondaryPeer(getState(), name);
        if (typeof secondaryPeer?.dispose === 'function') {
            secondaryPeer.dispose();
            dispatch(setVideoMemberSecondaryPeerAction(name, {}));
        }
    });

    dispatch(sendMessage({ id: 'leaveRoom' }));
};

const logSignallingMessage = (messageData) => (dispatch) => {
    if (messageData.length > 200) {
        dispatch(
            addMeetingLog(
                `VideoSignalingClientSocket#handleMessage: Got large message with id: ${
                    messageData && messageData.id
                }`,
            ),
        );
    } else {
        const messageJson = JSON.stringify(messageData);
        dispatch(
            addMeetingLog(
                `VideoSignalingClientSocket#handleMessage: Got message with data: ${messageJson}`,
            ),
        );
    }
};

// Room-state broadcasts (activity ordering, speaking set). Split out of the main
// signaling switch below to keep that switch's complexity in check; returns true
// when it has handled the message.
const handleRoomStateMessage = (id, messageData, dispatch) => {
    switch (id) {
        case 'newActivity':
            dispatch(
                setVideoActivityListAction(fromJS(messageData.participants)),
            );
            return true;
        case 'awaitingParticipants':
            // Everyone asking to join, as join names, re-sent whole on every
            // change (VID-76). A full list rather than deltas means two people
            // allowing or denying at the same moment converge on the same
            // queue instead of racing.
            dispatch(
                setWaitingParticipantsAction(
                    fromJS(messageData.awaiting || []),
                ),
            );
            return true;
        case 'userSpeakingChanged':
            dispatch(
                setSpeakingMembersAction(fromJS(messageData.speakers || [])),
            );
            return true;
        default:
            return false;
    }
};

// eslint-disable-next-line complexity
const handleSignalingMessage = (message) => async (dispatch, getState) => {
    const messageData = JSON.parse(message.data);
    const id = messageData && messageData.id;

    if (!id) {
        dispatch(
            addMeetingLog(
                `VideoSignalingClientSocket#handleMessage: Got message with no id? ${message}`,
            ),
            MeetingLogLevel.WARNING,
        );
        return;
    }

    logSignallingMessage(messageData, dispatch);

    if (handleRoomStateMessage(id, messageData, dispatch)) {
        return;
    }

    switch (id) {
        case 'ping': {
            const uuid = messageData && messageData.uuid;
            if (uuid) {
                dispatch(sendMessage({ id: 'pong', uuid }));
            }
            break;
        }
        case 'awaitingApproval': {
            // We are external and the room is occupied, so someone in there has
            // to let us in (VID-76). Deliberately leaves the join promise
            // pending: the very same promise is resolved by the roomJoined that
            // follows an allow, or rejected by admissionDenied below.
            //
            // Asking is not a message of its own: signaling holds an ordinary
            // joinRoom that carried admissionAware, so the "Ask to join" button
            // sends the same thing "Join" does and the difference is only in
            // what comes back.
            dispatch(
                addMeetingLog(
                    'VideoSignalingClientSocket#awaitingApproval: held in the waiting room, awaiting a verdict',
                ),
            );
            dispatch(
                setOwnWaitingRoomStandingAction(WaitingRoomStanding.WAITING),
            );

            // Held when nobody asked to be. Home told this client it is a
            // colleague, and signaling — asking home the same question by uuid
            // — was told otherwise, so the two disagree and the wait is an
            // accident: the identity capture is written to a distributed cache
            // and read back by whichever pod answers, and a read that races or
            // times out fails closed to EXTERNAL. Nobody is coming to admit a
            // colleague nobody meant to hold, so end the wait now and let
            // joinMeeting try once more rather than sitting here for minutes.
            if (!selectJoinExpectedToBeHeld(getState())) {
                dispatch(
                    addMeetingLog(
                        'VideoSignalingClientSocket#awaitingApproval: held despite home calling us internal — abandoning the wait',
                        MeetingLogLevel.WARNING,
                    ),
                );
                const heldPromise = selectVideoJoinRoomPromise(getState());
                if (heldPromise) {
                    heldPromise.reject();
                    dispatch(setVideoJoinRoomPromiseAction(null));
                }
            }
            break;
        }
        case 'admissionDenied': {
            const cause = messageData.cause;
            dispatch(
                addMeetingLog(
                    `VideoSignalingClientSocket#admissionDenied: not let into the meeting, cause ${cause}`,
                    MeetingLogLevel.WARNING,
                ),
            );
            dispatch(
                setOwnWaitingRoomStandingAction(
                    WaitingRoomStanding.DENIED,
                    cause,
                ),
            );
            // Mirrors roomNotJoined: reject and clear, so the join unwinds
            // through joinMeeting's own error path rather than hanging.
            const promise = selectVideoJoinRoomPromise(getState());
            if (promise) {
                promise.reject();
                dispatch(setVideoJoinRoomPromiseAction(null));
            }
            break;
        }
        case 'roomJoined': {
            const promise = selectVideoJoinRoomPromise(getState());
            promise.resolve();
            dispatch(setVideoJoinRoomPromiseAction(null));
            // Whether we were held in the waiting room or walked straight in,
            // we are in now — nothing left to wait on.
            dispatch(setOwnWaitingRoomStandingAction(null));

            const reconnectionToken =
                messageData && messageData.reconnectionToken;
            dispatch(setVideoSenderReconnectionTokenAction(reconnectionToken));
            break;
        }
        case 'roomNotJoined': {
            const cause = messageData.cause;
            const roomName = messageData.room;
            dispatch(handleRoomNotJoined(roomName, cause));
            const promise = selectVideoJoinRoomPromise(getState());
            promise.reject();
            dispatch(setVideoJoinRoomPromiseAction(null));
            break;
        }
        case 'newParticipantArrived': {
            const memberName = messageData.name;
            // Read before the member is registered below, which would otherwise
            // make this true for everyone. A name we already hold is one the
            // server is re-announcing rather than a new arrival: names carry a
            // per-join UUID, so the same name can only ever be the same join
            // session — never a different person, and never a later join by the
            // same person.
            const memberIsAlreadyInMeeting = !!selectVideoMemberByName(
                getState(),
                memberName,
            );
            // Register the member (so a tile appears) but don't subscribe to
            // their video here — useVideoSubscriptions subscribes them once/if
            // their tile is actually visible. This keeps large meetings from
            // pulling a stream for everyone the moment they join.
            dispatch(
                addMeetingLog(
                    `VideoSignalingClientSocket#newParticipantArrived: ${memberName} joined — registered; video subscription deferred until their tile is visible`,
                ),
            );
            dispatch(setVideoNewMeetingVideoMemberAction(memberName));
            // Sounded from the arrival itself rather than from a component
            // watching the roster: this has to be heard while the meeting is in
            // the dock or behind other work, which is exactly when nothing that
            // would notice is on screen. 'existingParticipants' registers the
            // people already here and is deliberately silent — only an arrival
            // after we are in the room is someone joining us.
            //
            // Silent for a member coming back from a drop, which reaches us as
            // an ordinary arrival. The two conditions catch different halves of
            // that: a leave we saw and remembered (one peer's connection
            // blipped), and a name still in our roster (nobody's leave ever
            // reached us — the signaling server itself went away and the room
            // is reassembling around us).
            if (
                !memberIsAlreadyInMeeting &&
                !memberIsReturningFromReconnect(memberName)
            ) {
                playParticipantJoinedAlert();
            }
            dispatch(getMeetingDataWithDiffCheck());
            break;
        }
        case 'participantLeft': {
            const videoMemberName = messageData.name;
            dispatch(
                addMeetingLog(
                    `VideoSignalingClientSocket#participantLeft: ${videoMemberName} left — closing their peers and removing their tile`,
                ),
            );
            // Before the tile goes: a rejoin arrives as a fresh
            // 'newParticipantArrived' for this same name, and only a remembered
            // leave tells the two apart.
            rememberMemberLeft(videoMemberName);
            await dispatch(closePrimaryMemberPeer(videoMemberName));
            await dispatch(closeSecondaryMemberPeer(videoMemberName));
            dispatch(resetScreenShareMember(videoMemberName));

            dispatch(removeVideoMemberAction(videoMemberName));
            // TODO: #video look at ConferenceActions#updateMembers for "smarter polling"
            await dispatch(getMeetingDataWithDiffCheck());
            break;
        }
        case 'existingParticipants': {
            // Register every existing member so their tiles render, but leave the
            // video subscriptions to useVideoSubscriptions — it subscribes only to
            // the ones on screen, instead of opening a receiver peer for the whole
            // room at join time.
            dispatch(
                addMeetingLog(
                    `VideoSignalingClientSocket#existingParticipants: registering ${messageData.data.length} existing member(s) — video subscriptions deferred to the visible-tile manager: [${messageData.data.join(', ')}]`,
                ),
            );
            messageData.data.forEach((memberName) => {
                dispatch(setVideoNewMeetingVideoMemberAction(memberName));
            });
            break;
        }
        case 'trackEnabled': {
            const memberName = messageData.name;
            const trackKind = messageData.kind;
            await dispatch(toggleTrackChange(memberName, trackKind, true));
            break;
        }
        case 'trackDisabled': {
            const memberName = messageData.name;
            const trackKind = messageData.kind;
            await dispatch(toggleTrackChange(memberName, trackKind, false));
            break;
        }
        case 'secondarySenderSetupDone': {
            const memberName = messageData.name;
            dispatch(setupSecondaryReceiverPeer(memberName));
            break;
        }
        case 'restartVideoFrom': {
            const memberName = messageData.name;
            dispatch(setupPrimaryReceiverPeer(memberName));
            break;
        }
        case 'iceCandidate': {
            const { name, candidate } = messageData;
            const primaryPeer = dispatch(getPrimaryPeer(name));
            dispatch(
                addIceCandidate(primaryPeer, name, candidate, PeerType.PRIMARY),
            );
            break;
        }
        case 'secondarySenderIceCandidate': {
            const { candidate } = messageData;
            const senderName = selectSenderName(getState());
            const secondarySenderPeer = dispatch(getSecondaryPeer(senderName));
            dispatch(
                addIceCandidate(
                    secondarySenderPeer,
                    senderName,
                    candidate,
                    PeerType.SECONDARY,
                    false, // sender path: no flush path, so don't buffer (see addIceCandidate)
                ),
            );
            break;
        }
        case 'secondaryReceiverIceCandidate': {
            const { name, candidate } = messageData;
            const secondaryReceiverPeer = dispatch(getSecondaryPeer(name));
            dispatch(
                addIceCandidate(
                    secondaryReceiverPeer,
                    name,
                    candidate,
                    PeerType.SECONDARY,
                ),
            );
            break;
        }
        case 'receiveVideoAnswer': {
            const { name, sdpAnswer } = messageData;
            const primaryPeer = dispatch(getPrimaryPeer(name));
            dispatch(
                processSdpAnswer(
                    primaryPeer,
                    sdpAnswer,
                    name,
                    PeerType.PRIMARY,
                ),
            );
            break;
        }
        case 'receiveSecondarySenderVideoAnswer': {
            const { sdpAnswer } = messageData;
            const senderName = selectSenderName(getState());
            const secondarySenderPeer =
                selectVideoSecondarySenderPeer(getState());
            dispatch(
                processSdpAnswer(
                    secondarySenderPeer,
                    sdpAnswer,
                    senderName,
                    PeerType.SECONDARY,
                ),
            );
            break;
        }
        case 'receiveSecondaryReceiverVideoAnswer': {
            const { name, sdpAnswer } = messageData;
            const secondaryReceiverPeer = selectVideoMemberSecondaryPeer(
                getState(),
                name,
            );
            dispatch(
                processSdpAnswer(
                    secondaryReceiverPeer,
                    sdpAnswer,
                    name,
                    PeerType.SECONDARY,
                ),
            );
            break;
        }
        case 'screenShareAlreadyActive': {
            await dispatch(stopScreenShare());
            break;
        }
        case 'turnOffVideoRequested': {
            // Another participant asked us to turn off our camera. Force it off
            // (only if it's currently on); the user can turn it back on later.
            const videoActive = selectVideoSenderVideoActive(getState());
            const requestedBy = messageData.by;
            dispatch(
                addMeetingLog(
                    `VideoSignalingClientSocket#handleMessage: turnOffVideoRequested by ${requestedBy}, videoActive ${videoActive}`,
                ),
            );
            if (videoActive) {
                await dispatch(forceMuteSenderVideo());
            }
            break;
        }
        case 'muteAudioRequested': {
            // Another participant asked us to mute. Mute only if we're currently
            // unmuted; the user can unmute themselves again later.
            const audioActive = selectSenderAudioActive(getState());
            const requestedBy = messageData.by;
            dispatch(
                addMeetingLog(
                    `VideoSignalingClientSocket#handleMessage: muteAudioRequested by ${requestedBy}, audioActive ${audioActive}`,
                ),
            );
            if (audioActive) {
                dispatch(muteAudio());
            }
            break;
        }
        case 'removeParticipantRequested': {
            // Another participant removed us — end the meeting locally, and leave a notice standing in the lobby (VID-471).
            const requestedBy = sliceUUIDFromUserName(messageData.by);
            dispatch(
                addMeetingLog(
                    `VideoSignalingClientSocket#handleMessage: removeParticipantRequested by ${messageData.by}`,
                ),
            );
            dispatch(
                setSnackbar({
                    i18nextMessage: 'videoconference.host_controls.removed_by',
                    i18nextMessageProps: { by: requestedBy },
                }),
            );
            dispatch(
                setOwnWaitingRoomStandingAction(
                    WaitingRoomStanding.REMOVED,
                    requestedBy,
                ),
            );
            await dispatch(endMeeting());
            break;
        }
        default: {
            break;
        }
    }
};

const resetScreenShareMember = (memberName) => (dispatch, getState) => {
    const screenShareMemberName =
        selectActiveMeetingScreenShareMemberName(getState());
    if (memberName === screenShareMemberName) {
        dispatch(setActiveMeetingScreenShareMemberNameAction(null));
    }
};

// ICE candidates can arrive from the server before the receive peer for that
// member exists — the primaryPeer/secondaryPeer slot still holds the empty
// placeholder ({}), which has no addIceCandidate. This is especially likely
// while KMS endpoint setup is slow (the placeholder window is large). Buffer
// such candidates per member + peer type and flush them once the peer is
// created (flushPendingIceCandidates), instead of dropping them or throwing
// "addIceCandidate is not a function" on the placeholder.
const pendingIceCandidates = new Map();
const iceBufferKey = (name, peerVersion) => `${peerVersion}:${name}`;
const isReceiverPeerReady = (peer) =>
    !!peer && typeof peer.addIceCandidate === 'function';

const addIceCandidate =
    (peer, name, iceCandidate, peerVersion, shouldBuffer = true) =>
    (dispatch) => {
        if (!isReceiverPeerReady(peer)) {
            if (!shouldBuffer) {
                // Sender-side ICE (e.g. our own screen-share sender): there is no
                // receiver-peer flush path for it, so buffering would leak and
                // never be applied. Keep the old behavior — log and drop; the
                // sender peer normally exists well before its ICE returns.
                dispatch(
                    addMeetingLog(
                        `VideoSignalingClientSocket#addIceCandidate: sender peer not ready, dropping ${peerVersion} ICE candidate on ${name}`,
                    ),
                );
                return;
            }
            // Peer not created yet (slot is still the empty placeholder). Buffer
            // the candidate; flushPendingIceCandidates applies it once the peer
            // is set up.
            const key = iceBufferKey(name, peerVersion);
            const buffered = pendingIceCandidates.get(key) || [];
            buffered.push(iceCandidate);
            pendingIceCandidates.set(key, buffered);
            dispatch(
                addMeetingLog(
                    `VideoSignalingClientSocket#addIceCandidate: peer not ready, buffering ${peerVersion} ICE candidate on ${name} (${buffered.length} pending)`,
                ),
            );
            return;
        }
        const rtcIceCandidate = new RTCIceCandidate(iceCandidate);
        try {
            peer.addIceCandidate(rtcIceCandidate, (error) => {
                if (error) {
                    dispatch(
                        addMeetingLog(
                            `VideoSignalingClientSocket#addIceCandidate: Error adding ${peerVersion} ICE candidate on ${name}, iceCandidate: ${rtcIceCandidate}`,
                            MeetingLogLevel.ERROR,
                        ),
                    );
                }
            });
        } catch (e) {
            dispatch(
                addMeetingLog(
                    `VideoSignalingClientSocket#addIceCandidate: Caught an error adding ${peerVersion} ICE candidate on ${name} - error ${e}`,
                    MeetingLogLevel.ERROR,
                ),
            );
        }
    };

// Apply any ICE candidates that arrived before the receive peer was ready, then
// clear the buffer. Called right after the peer is created.
const flushPendingIceCandidates = (name, peerVersion) => (dispatch) => {
    const key = iceBufferKey(name, peerVersion);
    const buffered = pendingIceCandidates.get(key);
    if (!buffered || buffered.length === 0) {
        return;
    }
    pendingIceCandidates.delete(key);
    const peer = dispatch(
        peerVersion === PeerType.SECONDARY
            ? getSecondaryPeer(name)
            : getPrimaryPeer(name),
    );
    buffered.forEach((candidate) =>
        dispatch(addIceCandidate(peer, name, candidate, peerVersion)),
    );
};

// Drop buffered ICE candidates for a member+peer (on close) so stale candidates
// from a previous session aren't applied to a later re-subscription.
const clearPendingIceCandidates = (name, peerVersion) => {
    pendingIceCandidates.delete(iceBufferKey(name, peerVersion));
};

const processSdpAnswer =
    (peer, sdpAnswer, name, peerVersion) => (dispatch, getState) => {
        if (peer) {
            try {
                peer.processAnswer(sdpAnswer, (error) => {
                    if (error) {
                        dispatch(
                            addMeetingLog(
                                `VideoSignalingClientSocket#addIceCandidate: Error processing answer on ${peerVersion} peer for ${name}, sdpAnswer: ${sdpAnswer}, error: ${error},`,
                                MeetingLogLevel.ERROR,
                            ),
                        );
                        const senderName = selectSenderName(getState());
                        const isReceiverPeer = senderName !== name;
                        if (
                            peerVersion === PeerType.PRIMARY &&
                            isReceiverPeer
                        ) {
                            dispatch(
                                addMeetingLog(
                                    `Retrying receiver peer for ${name}`,
                                ),
                            );
                            dispatch(restartPrimaryReceiverPeer(name));
                        } else {
                            dispatch(
                                addMeetingLog(
                                    `Not attempting retry of own sender peer of type ${peerVersion} receiver direction ${isReceiverPeer}`,
                                ),
                            );
                        }
                    }
                });
            } catch (e) {
                dispatch(
                    addMeetingLog(
                        `VideoSignalingClientSocket#addIceCandidate: Caught an error processing answer on ${peerVersion} peer for ${name} - error ${e}`,
                        MeetingLogLevel.ERROR,
                    ),
                );
            }
        } else {
            dispatch(
                addMeetingLog(
                    `VideoSignalingClientSocket#addIceCandidate: No peer when processing answer on ${peerVersion} peer for ${name}`,
                    MeetingLogLevel.ERROR,
                ),
            );
        }
    };

const getPrimaryPeer = (name) => (_dispatch, getState) => {
    const senderName = selectSenderName(getState());
    if (senderName === name) {
        return selectVideoSenderPrimaryPeer(getState());
    } else {
        return selectVideoMemberPrimaryPeer(getState(), name);
    }
};

const getSecondaryPeer = (name) => (_dispatch, getState) => {
    const state = getState();
    const senderName = selectSenderName(state);
    if (name === senderName) {
        return selectVideoSecondarySenderPeer(state);
    } else {
        return selectVideoMemberSecondaryPeer(state, name);
    }
};

export const restartPrimaryReceiverPeer =
    (memberName) => (dispatch, getState) => {
        const peerSetupAttempts = selectVideoMemberPeerSetupAttempts(
            getState(),
            memberName,
        );
        if (peerSetupAttempts < 2) {
            dispatch(
                setupPeerAndIncreaseAttemptCounter(
                    memberName,
                    peerSetupAttempts,
                ),
            );
        } else if (peerSetupAttempts < 5) {
            setTimeout(
                () =>
                    dispatch(
                        setupPeerAndIncreaseAttemptCounter(
                            memberName,
                            peerSetupAttempts,
                        ),
                    ),
                500 * peerSetupAttempts,
            );
        } else {
            dispatch(
                addMeetingLog(
                    `Attempted to set up receiver peer for ${memberName} failed 5 times - stopped`,
                ),
                MeetingLogLevel.ERROR,
            );
        }
    };

const setupPeerAndIncreaseAttemptCounter =
    (memberName, numberOfAttempts) => (dispatch) => {
        const increasedNumberOfAttempts = (numberOfAttempts || 0) + 1;
        dispatch(
            setVideoMemberPeerSetupAttemptsAction(
                memberName,
                increasedNumberOfAttempts,
            ),
        );
        dispatch(
            addMeetingLog(
                `VideoSignalingClientSocket#setupPeerAndIncreaseAttemptCounter, try to restart video from ${memberName}, attempt ${increasedNumberOfAttempts}`,
                MeetingLogLevel.WARNING,
            ),
        );
        dispatch(setupPrimaryReceiverPeer(memberName));
    };

/**
 * This method makes a API call and checks that there has been a change in the member
 * count, as if the database has not yet updated when we ask home for the data, no diff
 * in API data can be the outcome, even if we expect a diff.
 */
const getMeetingDataWithDiffCheck = () => async (dispatch, getState) => {
    const meetingCode = selectActiveMeetingCode(getState());
    dispatch(
        addMeetingLog(
            `VideoSignalingClientSocket#getMeetingDataWithDiffCheck, updating members in room with meetingCode: ${meetingCode}`,
        ),
    );
    const prevActiveParticipantsCount =
        selectActiveMeetingMembers(getState()).size;
    await dispatch(fetchMeetingData(meetingCode));
    const activeParticipantsCount = selectActiveMeetingMembers(getState()).size;
    if (prevActiveParticipantsCount === activeParticipantsCount) {
        await new Promise((resolve) =>
            setTimeout(async () => {
                await dispatch(fetchMeetingData(meetingCode));
                resolve();
            }, 1000),
        );
    }
};

export const sendMessage = (messageObject) => (dispatch, getState) => {
    const webSocket = selectVideoWebSocket(getState());
    const readyState = webSocket && webSocket.readyState;
    const messageJson = JSON.stringify(messageObject);
    // Don't echo these to the meeting log: 'log' would recurse, and the
    // high-frequency 'userSpeaking' signal (start/stop edges plus a 1s
    // keepalive) would flood it.
    const skipMeetingLog =
        messageObject &&
        messageObject.id &&
        (messageObject.id === 'log' || messageObject.id === 'userSpeaking');
    if (readyState === 1) {
        if (!skipMeetingLog) {
            //If we log our log we will end up in a recursive infinite loop.
            if (messageJson.length > 200) {
                dispatch(
                    addMeetingLog(
                        `VideoSignalingClientSocket#sendMessage, sending message with id ${messageObject.id}`,
                    ),
                );
            } else {
                dispatch(
                    addMeetingLog(
                        `VideoSignalingClientSocket#sendMessage, sending message ${messageJson}`,
                    ),
                );
            }
        }
        webSocket.send(messageJson);
    } else {
        if (!skipMeetingLog) {
            //If we log our log we will end up in a recursive infinite loop.
            if (messageObject.length > 200) {
                dispatch(
                    addMeetingLog(
                        `VideoSignalingClientSocket#sendMessage, Not sending message with id, ${messageObject.id} as state is not ready`,
                        MeetingLogLevel.WARNING,
                    ),
                );
            } else {
                dispatch(
                    addMeetingLog(
                        `VideoSignalingClientSocket#sendMessage, Not sending message, ${messageJson} as state is not ready`,
                        MeetingLogLevel.WARNING,
                    ),
                );
            }
        }
    }
};

export const setupPrimarySenderPeerAndRTCListener =
    (name, stream) => async (dispatch) => {
        const senderPeer = await dispatch(setupPrimarySenderPeer(name, stream));

        const senderRtcPeerConnection = senderPeer && senderPeer.peerConnection;
        dispatch(
            rtcStateChangeEventHandler(
                senderRtcPeerConnection,
                setVideoConnectionStateAction,
            ),
        );
    };

const setupPrimarySenderPeer =
    (senderName, stream) => async (dispatch, getState) => {
        const videoActive = selectVideoSenderVideoActive(getState());
        const screenShareActive =
            selectVideoSenderScreenShareActive(getState());
        const shouldHaveMedia = videoActive || screenShareActive;
        dispatch(
            addMeetingLog(
                `VideoSignalingClientSocket#setupPrimarySenderPeer, will try to setup primary peer for ${senderName}, has stream: ${!!stream}, has video active: ${videoActive}`,
            ),
        );
        let mediaStream;
        if (stream) {
            mediaStream = stream;
        } else {
            mediaStream = selectVideoSenderVideoStream(getState());
        }

        const onIceCandidateCallback = (candidate) =>
            dispatch(
                sendMessage({
                    id: 'onIceCandidate',
                    name: senderName,
                    candidate,
                }),
            );

        const offerToReceiveVideoCallback = (_unusedVariable, sdpOffer) =>
            dispatch(
                sendMessage({
                    id: 'setupOwnVideoUpstream',
                    sdpOffer,
                }),
            );

        try {
            const newSenderPeer = await dispatch(
                setupSenderPeer(
                    senderName,
                    shouldHaveMedia,
                    mediaStream,
                    onIceCandidateCallback,
                    offerToReceiveVideoCallback,
                    screenShareActive,
                ),
            );
            dispatch(setVideoSenderPrimaryPeerAction(newSenderPeer));
            return newSenderPeer;
        } catch (e) {
            dispatch(
                addMeetingLog(
                    `VideoSignalingClient#setupPrimarySenderPeer could not set up primary sender peer error, ${e}`,
                    MeetingLogLevel.ERROR,
                ),
            );
            return null;
        }
    };

export const setupSecondarySenderPeer =
    (senderName) => async (dispatch, getState) => {
        const videoActive = selectVideoSenderVideoActive(getState());
        const secondaryMediaStream =
            selectVideoSecondarySenderVideoStream(getState());
        dispatch(
            addMeetingLog(
                `VideoSignalingClientSocket#setupSecondarySenderPeer, will try to setup secondary peer for ${senderName}, has video active: ${videoActive}`,
            ),
        );

        const onIceCandidateCallback = (candidate) =>
            dispatch(
                sendMessage({
                    id: 'secondarySenderOnIceCandidate',
                    candidate,
                }),
            );

        const offerToReceiveVideoCallback = (_unusedVariable, sdpOffer) =>
            dispatch(
                sendMessage({
                    id: 'setupSecondarySenderVideo',
                    sdpOffer,
                }),
            );

        try {
            const newSenderPeer = await dispatch(
                setupSenderPeer(
                    senderName,
                    videoActive,
                    secondaryMediaStream,
                    onIceCandidateCallback,
                    offerToReceiveVideoCallback,
                ),
            );
            dispatch(setVideoSenderSecondaryPeerAction(newSenderPeer));
        } catch (e) {
            dispatch(
                addMeetingLog(
                    `VideoSignalingClient#setupSecondarySenderPeer could not set up secondary sender peer, ${e}`,
                    MeetingLogLevel.ERROR,
                ),
            );
        }
    };

const setupSenderPeer =
    (
        senderName,
        shouldHaveMedia,
        mediaStream,
        onIceCandidateFunction,
        offerToReceiveVideoFunction,
        screenShareActive = false,
    ) =>
    async (dispatch) => {
        return new Promise((resolve, reject) => {
            if (!senderName) {
                dispatch(
                    addMeetingLog(
                        'VideoSignalingClientSocket#setupSenderPeer, no name to setup peer for',
                        MeetingLogLevel.ERROR,
                    ),
                );
                return reject('No sender name');
            }
            if (!shouldHaveMedia) {
                dispatch(
                    addMeetingLog(
                        'VideoSignalingClientSocket#setupSenderPeer, should not have any media, will not set up a peer',
                        MeetingLogLevel.WARNING,
                    ),
                );
                resolve(null);
            }
            if (!mediaStream && shouldHaveMedia) {
                dispatch(
                    addMeetingLog(
                        'VideoSignalingClientSocket#setupSenderPeer, got no media stream despite that we should',
                        MeetingLogLevel.ERROR,
                    ),
                );
                return reject('No sender video stream');
            }
            const options = {
                videoStream: mediaStream,
                mediaConstraints: { audio: false, video: true },
                onIceCandidateFunction,
            };

            const peer = KurentoUtils.WebRtcPeer.WebRtcPeerSendonly(
                options,
                function (error) {
                    if (error) {
                        dispatch(
                            addMeetingLog(
                                `VideoSignalingClientSocket#setupSenderPeer, got an error, ${error.message}`,
                                MeetingLogLevel.ERROR,
                            ),
                        );
                        return reject(error);
                    }
                    if (screenShareActive) {
                        /*
                         * Cap the screen-share resolution/frame rate so a Retina/4K
                         * capture stays decodable on low-power and mobile viewers
                         * (iOS Safari software-decodes video). The codec is left at
                         * the browser default.
                         */
                        applyScreenShareEncoding(peer.peerConnection);
                    }
                    peer.generateOffer(offerToReceiveVideoFunction);
                    dispatch(stopScreenShareFromChromePrompt(peer));
                    dispatch(
                        addMeetingLog(
                            `VideoSignalingClientSocket#setupSenderPeer, successfully set up peer for ${senderName}`,
                        ),
                    );
                    return resolve(peer);
                },
            );
            return null;
        });
    };

export const setupPrimaryReceiverPeer =
    (memberName) => async (dispatch, getState) => {
        dispatch(
            addMeetingLog(
                `VideoSignalingClientSocket#setupPrimaryReceiverPeer, set up primary peer for ${memberName}`,
            ),
        );
        const oldPrimaryReceiverPeer = selectVideoMemberPrimaryPeer(
            getState(),
            memberName,
        );
        // Only tear down a real peer; both placeholder forms (empty Immutable Map
        // on init, plain {} after a prior close) have no addIceCandidate and need
        // no teardown. Using .isEmpty() alone would treat the plain {} as a live
        // peer and fire a spurious close/cancelVideoFrom.
        if (isReceiverPeerReady(oldPrimaryReceiverPeer)) {
            await dispatch(closePrimaryMemberPeer(memberName));
        }
        const onIceCandidateCallback = (candidate) =>
            dispatch(
                sendMessage({
                    id: 'onIceCandidate',
                    name: memberName,
                    candidate,
                }),
            );
        const offerToReceiveVideoCallback = (_unusedVariable, sdpOffer) =>
            dispatch(
                sendMessage({
                    id: 'setupVideoStreamFromMember',
                    fromMemberName: memberName,
                    /*
                     * Prefer VP8 on the primary receive leg. When a member shares
                     * their screen, the screen goes out on the PRIMARY sender (the
                     * camera moves to the secondary), so the screen is received here.
                     * iOS Safari otherwise negotiates H.264, forcing the SFU to
                     * transcode the VP8 source — and that transcode emits no keyframe
                     * on subscribe, so a mid-share `restartVideo` leaves the viewer on
                     * a black tile. Offering VP8 first makes the SFU forward instead.
                     */
                    sdpOffer: preferVp8InSdp(sdpOffer),
                }),
            );

        try {
            const newReceiverPeer = await dispatch(
                setupReceiverPeer(
                    memberName,
                    onIceCandidateCallback,
                    offerToReceiveVideoCallback,
                    PeerType.PRIMARY,
                ),
            );
            dispatch(
                setVideoMemberPrimaryPeerAction(memberName, newReceiverPeer),
            );
            dispatch(flushPendingIceCandidates(memberName, PeerType.PRIMARY));
            // Setup succeeded — reset the attempt counter to hide the
            // RestartVideoPlate. The visible-tile manager (useVideoSubscriptions)
            // tracks its own subscribe intent, so this rebuilt peer doesn't race
            // it. Only write when nonzero so the common path doesn't touch the
            // store.
            if (
                selectVideoMemberPeerSetupAttempts(getState(), memberName) > 0
            ) {
                dispatch(setVideoMemberPeerSetupAttemptsAction(memberName, 0));
            }
        } catch (e) {
            // Setup failed after candidates may have been buffered; drop them so a
            // later re-subscribe doesn't replay candidates from this dead peer.
            clearPendingIceCandidates(memberName, PeerType.PRIMARY);
            // And drop the stats entry, if a partially-succeeded setup created
            // one. Without this, the last attempt of the bounded restart loop
            // leaves a dead peer registered for the rest of the meeting —
            // getStats()-polled every tick and misreporting a setup failure as
            // a zero-rate stream. A successful retry re-registers.
            removePeerFromStats(memberName);
            dispatch(
                addMeetingLog(
                    `VideoSignalingClient#setupPrimaryReceiverPeer could not set up receiver peer, ${e}`,
                    MeetingLogLevel.ERROR,
                ),
            );
            // A swallowed failure stores no peer and never touches
            // peerSetupAttempts, so nothing would retry this member: the
            // visible-tile manager (useVideoSubscriptions) marks the member
            // subscribed once it dispatches setup and won't refire on its own,
            // and the manual RestartVideoPlate needs the attempt counter to move.
            // Feed the existing bounded restart loop — 5 attempts with backoff,
            // then the plate takes over.
            dispatch(restartPrimaryReceiverPeer(memberName));
        }
    };

const setupSecondaryReceiverPeer =
    (memberName) => async (dispatch, getState) => {
        dispatch(
            addMeetingLog(
                `VideoSignalingClientSocket#setupSecondaryReceiverPeer, set up secondary peer for ${memberName}`,
            ),
        );
        const oldSecondaryReceiverPeer = selectVideoMemberSecondaryPeer(
            getState(),
            memberName,
        );
        // Only tear down a real peer; the placeholder forms (empty Immutable Map,
        // plain {}) have no addIceCandidate and need no teardown (see the primary
        // receiver setup for the rationale).
        if (isReceiverPeerReady(oldSecondaryReceiverPeer)) {
            await dispatch(closeSecondaryMemberPeer(memberName));
        }

        const onIceCandidateCallback = (candidate) =>
            dispatch(
                sendMessage({
                    id: 'secondaryReceiverOnIceCandidate',
                    name: memberName,
                    candidate,
                }),
            );

        const offerToReceiveVideoCallback = (_unusedVariable, sdpOffer) =>
            dispatch(
                sendMessage({
                    id: 'setupSecondaryReceiverVideo',
                    name: memberName,
                    /*
                     * Prefer VP8 on the secondary receive leg (the sharer's
                     * camera while screen sharing) for the same reason as the
                     * primary leg: make the SFU forward the VP8 source instead of
                     * transcoding it to H.264 for iOS Safari (whose transcode
                     * emits no keyframe on subscribe).
                     */
                    sdpOffer: preferVp8InSdp(sdpOffer),
                }),
            );
        try {
            const newReceiverPeer = await dispatch(
                setupReceiverPeer(
                    memberName,
                    onIceCandidateCallback,
                    offerToReceiveVideoCallback,
                    PeerType.SECONDARY,
                ),
            );
            dispatch(
                setVideoMemberSecondaryPeerAction(memberName, newReceiverPeer),
            );
            dispatch(flushPendingIceCandidates(memberName, PeerType.SECONDARY));
        } catch (e) {
            // Setup failed after candidates may have been buffered; drop them so a
            // later re-subscribe doesn't replay candidates from this dead peer.
            clearPendingIceCandidates(memberName, PeerType.SECONDARY);
            dispatch(
                addMeetingLog(
                    `VideoSignalingClient#setupSecondaryReceiverPeer could not set up receiver peer, ${e}`,
                    MeetingLogLevel.ERROR,
                ),
            );
        }
    };

const setupReceiverPeer =
    (
        memberName,
        onIceCandidateFunction,
        offerToReceiveVideoFunction,
        peerType,
    ) =>
    (dispatch) => {
        return new Promise((resolve, reject) => {
            const options = {
                mediaConstraints: {
                    // Video-only: meeting audio is carried over the separate SIP
                    // call (see meeting-call-client.js), not these Kurento receive
                    // legs, and the video tiles render muted. Requesting audio here
                    // made the SFU forward an audio stream that is never played —
                    // pure waste, and extra load on the media server. Keep it off.
                    audio: false,
                    video: {
                        width: { min: 352, ideal: 640, max: 640 },
                        height: { ideal: 480, max: 480 },
                        frameRate: { ideal: CANVAS_SETTINGS.FRAME_RATE },
                    },
                },
                onicecandidate: onIceCandidateFunction,
            };
            const peer = KurentoUtils.WebRtcPeer.WebRtcPeerRecvonly(
                options,
                function (error) {
                    if (error) {
                        dispatch(
                            addMeetingLog(
                                `VideoSignalingClientSocket#setupReceiverPeer, got an error when trying to setup receiver peer for ${memberName}`,
                            ),
                        );
                        return reject(error);
                    }
                    peer.generateOffer(offerToReceiveVideoFunction);
                    dispatch(
                        addMeetingLog(
                            `VideoSignalingClientSocket#setupReceiverPeer, successfully set up a receiver peer for ${memberName}`,
                        ),
                    );
                    // Register only once setup reports success — registering in
                    // the executor put failed peers into the stats store, where
                    // nothing on the failure path ever removed them.
                    //
                    // Primary receive legs only: both peers of a member share
                    // the same name, so registering the secondary too would
                    // overwrite the primary's entry and the tile read-out
                    // (keyed by member name) would silently switch to
                    // camera-during-screen-share numbers.
                    if (
                        peerType === PeerType.PRIMARY &&
                        isVideoStatsDebugEnabled()
                    ) {
                        addPeerForStats(peer.peerConnection, memberName);
                    }
                    return resolve(peer);
                },
            );
        });
    };

export const closePrimaryMemberPeer =
    (memberName) => async (dispatch, getState) => {
        dispatch(
            addMeetingLog(
                `VideoSignalingClientSocket#closePrimaryMemberPeer, closing primary peer belonging to ${memberName}`,
            ),
        );
        const oldReceiverPrimaryPeer = selectVideoMemberPrimaryPeer(
            getState(),
            memberName,
        );
        try {
            await dispatch(
                closeReceiverPeer(
                    oldReceiverPrimaryPeer,
                    memberName,
                    PeerType.PRIMARY,
                ),
            );
        } catch (e) {
            dispatch(
                addMeetingLog(
                    `VideoSignalingClientSocket#closePrimaryMemberPeer, caught an error when closing primary peer belonging to ${memberName}, ${e}`,
                ),
            );
        }

        dispatch(
            sendMessage({
                id: 'cancelVideoFrom',
                sender: memberName,
            }),
        );
        dispatch(setVideoMemberPrimaryPeerAction(memberName, {}));
        dispatch(
            addMeetingLog(
                `VideoSignalingClientSocket#closePrimaryMemberPeer, successfully closed primary peer belonging to ${memberName}`,
            ),
        );
    };

export const closeSecondaryMemberPeer =
    (memberName) => async (dispatch, getState) => {
        dispatch(
            addMeetingLog(
                `VideoSignalingClientSocket#closeSecondaryMemberPeer, closing secondary peer belonging to ${memberName}`,
            ),
        );
        const oldSecondaryReceiverPeer = selectVideoMemberSecondaryPeer(
            getState(),
            memberName,
        );
        try {
            await dispatch(
                closeReceiverPeer(
                    oldSecondaryReceiverPeer,
                    memberName,
                    PeerType.SECONDARY,
                ),
            );
        } catch (e) {
            dispatch(
                addMeetingLog(
                    `VideoSignalingClientSocket#closeSecondaryMemberPeer, caught an error when closing secondary peer belonging to ${memberName}, ${e}`,
                ),
            );
        }

        dispatch(
            sendMessage({
                id: 'cancelSecondaryVideoFrom',
                sender: memberName,
            }),
        );
        dispatch(setVideoMemberSecondaryPeerAction(memberName, {}));
        dispatch(
            addMeetingLog(
                `VideoSignalingClientSocket#closeSecondaryMemberPeer, successfully closed secondary peer belonging to ${memberName}`,
            ),
        );
    };

const closeReceiverPeer = (oldPeer, name, peerType) => (dispatch) => {
    // Only the primary peer is registered for stats (see setupReceiverPeer);
    // removing on a secondary close would delete the primary's entry, since
    // both are keyed by the member name.
    if (peerType === PeerType.PRIMARY) {
        removePeerFromStats(name);
    }
    clearPendingIceCandidates(name, peerType);

    return new Promise((resolve, reject) => {
        if (!oldPeer) {
            //We have no peer to close
            dispatch(
                addMeetingLog(
                    `VideoSignalingClientSocket#closeReceiverPeer, there was no ${peerType} peer to close for ${name}`,
                ),
            );
            return resolve();
        } else {
            try {
                oldPeer.dispose();
            } catch (e) {
                dispatch(
                    addMeetingLog(
                        `VideoSignalingClientSocket#closeReceiverPeer, tried disposing ${peerType} peer for ${name} but could not do it, ${e}`,
                    ),
                    MeetingLogLevel.WARNING,
                );
                return reject('Could not dispose peer');
            }
            return resolve();
        }
    });
};

const stopScreenShareFromChromePrompt = (peer) => (dispatch, getState) => {
    const senders = peer.peerConnection.getSenders();
    senders.forEach(
        (sender) =>
            (sender.track.onended = () => {
                const screenShareActive =
                    selectVideoSenderScreenShareActive(getState());
                if (screenShareActive) {
                    dispatch(
                        addMeetingLog(
                            'VideoSignalingClientSocket#stopScreenShareFromChromePrompt, closing sender screen share initiated from the chrome prompt',
                        ),
                    );
                    dispatch(stopScreenShare());
                }
            }),
    );
};
